import { StatusBar } from 'expo-status-bar';
import useMyVaroData, {
  LoadingState,
} from 'features/myVaro/hooks/useMyVaroData';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Animated, {
  runOnJS,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import { isWeb } from 'shared/constants/platform';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

function MyVaroOverlay({
  loadingState,
  isVisible,
  errorMessage,
  refresh,
}: {
  loadingState: LoadingState;
  isVisible: boolean;
  errorMessage?: string;
  refresh: () => void;
}) {
  // default visible:
  const opacity = useSharedValue(1);
  const [overlayInTree, setOverlayInTree] = useState(true);
  const { defaultTitle } = useDefaultErrorStrings();
  const insets = useSafeAreaInsets();

  const val = isVisible ? 1 : 0;
  if (!overlayInTree && isVisible) {
    setOverlayInTree(true);
  }

  const hideOverlay = () => {
    setOverlayInTree(false);
  };

  opacity.value = withTiming(val, { duration: 100 }, (isFinished) => {
    if (isFinished && overlayInTree && !isVisible) {
      runOnJS(hideOverlay)();
    }
  });

  if (!overlayInTree) {
    return null;
  }

  return (
    <Animated.View style={{ ...styles.overlayContainer, opacity }}>
      <ScreenTransitionOverlay
        isLoading={loadingState === 'loading'}
        LoadingComponent={
          <View style={{ paddingTop: insets.top }}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          errorMessage && !loadingState ? (
            <DefaultPageDownErrorMessage
              error={errorMessage}
              title={defaultTitle}
              message={errorMessage}
              tryAgainAction={refresh}
            />
          ) : null
        }
      />
    </Animated.View>
  );
}

function MyVaro() {
  const { t: translatedText } = useTranslation('myVaro');
  const { loadingState, response, errorModel, refresh } = useMyVaroData();
  const isLoading = loadingState === 'loading';
  const isError = Boolean(errorModel?.messageBody);

  return (
    <>
      <StatusBar style="dark" />
      <View style={styles.container}>
        <View style={styles.headerContainer}>
          <NavigationBar title="" empty />
        </View>
        <SectionedContentScreen
          maskHeaderFromAmplitude={true}
          initialNumToRender={25}
          model={{
            header: {
              id: 'myVaroHeader',
              render: () => (
                <ExpressiveSmText style={styles.headerText} numberOfLines={1}>
                  {translatedText('title')}
                </ExpressiveSmText>
              ),
            },
            ...(response?.sectionList ?? { sections: [] }),
          }}
        />

        <MyVaroOverlay
          loadingState={loadingState}
          isVisible={isError || isLoading}
          errorMessage={errorModel?.messageBody}
          refresh={() => refresh(false)}
        />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    zIndex: 1,
  },
  headerContainer: {
    backgroundColor: theme.color.background.primary,
    paddingTop: isWeb ? theme.spacing.xl : 0,
  },
  headerText: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  overlayContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.tertiary,
    height: '100%',
    justifyContent: 'flex-start',
    position: 'absolute',
    width: '100%',
    zIndex: 2,
  },
});

export default MyVaro;
